import { useEffect, useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { track } from '@/lib/analytics';

const FaqItem = ({ item, index, open, onToggle }) => {
  const id = useId();
  return (
    <div className="rounded-[28px] bg-accent text-accent-foreground" data-testid={`faq-page-item-${index}`}>
      <h2>
        <button
          type="button"
          id={`${id}-q`}
          aria-expanded={open}
          aria-controls={`${id}-a`}
          onClick={onToggle}
          className="flex min-h-[72px] w-full items-center justify-between gap-4 px-6 py-4 text-left text-[17px] font-medium md:px-8 md:text-[19px]"
        >
          {item.question}
          <ChevronDown
            className={`h-5 w-5 flex-none transition-transform duration-200 ease-out ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
      </h2>
      <div
        id={`${id}-a`}
        role="region"
        aria-labelledby={`${id}-q`}
        className={`grid transition-[grid-template-rows,visibility] duration-200 ease-out ${open ? 'visible grid-rows-[1fr]' : 'invisible grid-rows-[0fr]'}`}
      >
        <div className="overflow-hidden">
          <p className="px-6 pb-6 text-[16px] leading-relaxed text-accent-foreground/85 md:px-8 md:text-[17px]">{item.answer}</p>
        </div>
      </div>
    </div>
  );
};

export const FaqPage = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => new Set());
  const page = t('info.faq', { returnObjects: true });

  useEffect(() => {
    document.title = t('meta.infoTitle', { name: page.title });
  }, [t, page.title]);

  // Any number of answers can be open at once
  const toggle = (i) => {
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(i)) next.delete(i);
      else {
        next.add(i);
        track('faq_open', { question_index: i, page: 'faq' });
      }
      return next;
    });
  };

  return (
    <main className="mx-auto max-w-[760px] px-4 pb-16 pt-8 md:px-6 md:pt-14" data-testid="faq-page">
      <h1 className="text-center font-heading text-[40px] font-extrabold leading-none tracking-[-0.035em] md:text-[56px]">{page.title}</h1>
      <div className="mt-8 flex flex-col gap-4 md:mt-12">
        {page.items.map((item, i) => (
          <FaqItem key={item.question} item={item} index={i} open={open.has(i)} onToggle={() => toggle(i)} />
        ))}
      </div>
    </main>
  );
};
